import { createContext, useContext, useEffect, useMemo, useState } from "react";

const StoreCtx = createContext(null);

export const StoreProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("bz_lang") || "fr");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bz_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("bz_lang", lang);
    document.documentElement.lang = lang;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("bz_cart", JSON.stringify(cart));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const addToCart = (product, { size, color, quantity = 1 }) => {
    const key = `${product.slug}::${size || ""}::${color || ""}`;
    setCart((prev) => {
      const found = prev.find((it) => it.key === key);
      if (found) {
        return prev.map((it) =>
          it.key === key ? { ...it, quantity: it.quantity + quantity } : it
        );
      }
      return [
        ...prev,
        {
          key,
          slug: product.slug,
          name_fr: product.name_fr,
          name_en: product.name_en,
          price_gnf: product.price_gnf,
          image: product.images?.[0],
          size,
          color,
          quantity,
        },
      ];
    });
    setCartOpen(true);
  };

  const removeFromCart = (key) =>
    setCart((prev) => prev.filter((it) => it.key !== key));

  const updateQty = (key, qty) =>
    setCart((prev) =>
      prev.map((it) => (it.key === key ? { ...it, quantity: Math.max(1, qty) } : it))
    );

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, it) => sum + it.quantity, 0);
  const cartTotal = cart.reduce((sum, it) => sum + it.quantity * it.price_gnf, 0);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      cart,
      cartOpen,
      setCartOpen,
      addToCart,
      removeFromCart,
      updateQty,
      clearCart,
      cartCount,
      cartTotal,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, cart, cartOpen, cartCount, cartTotal],
  );

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
